import { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Building, Floor, Town } from 'src/api/types';
import { FloorChipItem } from 'src/components/FloorChips/FloorChips';
import { FloorMapProvider } from 'src/components/FloorMap/FloorMapProvider';
import { Point } from 'src/components/FloorMap/types';
import { Page } from 'src/components/Page/Page';
import { useIdleTimer } from 'src/hooks/useIdleTimer';
import { useMapStyle } from 'src/hooks/useMapStyle';
import { FullscreenContainer } from 'src/providers/FullscreenProvider/FullscreenContainer/FullscreenContainer';
import { useFullscreenElement } from 'src/providers/FullscreenProvider/FullscreenProvider';
import {
  SearchBookingObjectProvider,
  useSearchBookingObject,
} from 'src/providers/SearchBookingObjectProvider';
import { SidesheetSlot } from 'src/providers/SidesheetSlot';
import { RoutePath } from 'src/routes/types';
import {
  getDeviceSize,
  getIsMobileWithTouchscreen,
} from 'src/store/agent/selectors';
import { getLastBookingsState } from 'src/store/lastBookings/selectors';
import { navigationActions } from 'src/store/navigation/slice';
import { getRoomLocation, getRoomsState } from 'src/store/rooms/selectors';
import { fetchRooms } from 'src/store/rooms/services';
import {
  openRoom,
  roomsActions,
  roomsPollingStarted,
  roomsPollingStopped,
} from 'src/store/rooms/slice';
import { getSidesheetMode } from 'src/store/sidesheet/selectors';
import { setSidesheetSlots, sidesheetActions } from 'src/store/sidesheet/slice';
import { SidesheetSlotItem } from 'src/store/sidesheet/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize } from 'src/types';

import { FloorChipsProvider } from '../../components/FloorChips/FloorChipsContext';

import { RoomBookingBottomSheet } from './RoomBookingBottomSheet/RoomBookingBottomSheet';
import { RoomBookingFloorMap } from './RoomBookingFloorMap';
import classes from './RoomBookingPage.module.scss';
import { RoomBookingPageActions } from './RoomBookingPageActions/RoomBookingPageActions';
import {
  RoomBookingPageMode,
  RoomBookingPageModeProvider,
  useRoomBookingPageMode,
} from './RoomBookingPageMode/RoomBookingPageModeProvider';
import { RoomMapSettingsBottomSheet } from './RoomMapSettingsBottomSheet/RoomMapSettingsBottomSheet';

const modalSidesheetSizes = new Set([
  DeviceSize.XXS,
  DeviceSize.XS,
  DeviceSize.S,
]);

const filterFloorItems = (item: FloorChipItem): boolean =>
  !item.value.isParking;

const PageLayout: FC = () => {
  const { mode } = useRoomBookingPageMode();
  const { fullscreen } = useFullscreenElement();
  const sidesheetMode = useSelector(getSidesheetMode);
  const [fixed, mapStyle] = useMapStyle();
  const isMobile = useSelector(getIsMobileWithTouchscreen);

  return (
    <Page
      actions={<RoomBookingPageActions />}
      testId='roomBooking'
      fullscreen={fullscreen}
      isSidesheetOpened={sidesheetMode === 'standard'}
      fixed={fixed}
      contentClassName={isMobile ? classes.mobile : undefined}
    >
      {mode === RoomBookingPageMode.Map ? (
        <>
          <div style={mapStyle}>
            <RoomBookingFloorMap />
          </div>
          {isMobile && (
            <>
              <RoomMapSettingsBottomSheet />
              <RoomBookingBottomSheet />
            </>
          )}
        </>
      ) : null}
    </Page>
  );
};

const InnerWithFloorMap: FC = () => {
  const size = useSelector(getDeviceSize);
  const { fullscreen } = useFullscreenElement();
  const { floor, loading } = useSelector(getRoomLocation);
  const { backgroundData, bookingObjects } = useSelector(getRoomsState);
  const { room } = useSelector(getLastBookingsState);

  const dispatch = useAppDispatch();

  const startPolling = useCallback(() => {
    dispatch(roomsPollingStarted(true));
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    dispatch(roomsPollingStopped());
  }, [dispatch]);

  useEffect(() => {
    const standardSlotId = fullscreen ? 'pageLevel' : 'tabsLevel';
    const modalSlotId = fullscreen ? 'pageLevelModal' : 'tabsLevelModal';
    const standardSlot: SidesheetSlotItem = {
      id: standardSlotId,
      mode: 'standard',
    };
    const modalSlot: SidesheetSlotItem = {
      id: modalSlotId,
      mode: 'modal',
    };
    const isModalSlot = modalSidesheetSizes.has(size);

    dispatch(
      setSidesheetSlots([
        {
          sidesheetId: 'roomBooking',
          slot: isModalSlot ? modalSlot : standardSlot,
        },
        {
          sidesheetId: 'roomOffice',
          slot: modalSlot,
        },
        {
          sidesheetId: 'roomsFilters',
          slot: isModalSlot ? modalSlot : standardSlot,
        },
      ]),
    );
    dispatch(
      navigationActions.setSegments([RoutePath.Main, RoutePath.RoomBooking]),
    );
  }, [dispatch, fullscreen, size]);

  const init = useCallback(async () => {
    if (floor?.floorId) {
      await dispatch(roomsPollingStopped());
      await dispatch(fetchRooms(true));
      await dispatch(roomsPollingStarted(false));
    }
  }, [dispatch, floor?.floorId]);

  useEffect(() => {
    init();
  }, [init]);

  useIdleTimer({
    idleMs: 60000,
    onPause: stopPolling,
    onResume: startPolling,
  });

  const { searchedBookingObjectRef, setSearchedBookingObject } =
    useSearchBookingObject();

  const laskRoomId = useMemo(() => {
    if (room && floor && room.floor.floorId === floor.floorId) {
      return room.placeId.toString();
    }
  }, [room, floor]);

  const searchedRoomId = searchedBookingObjectRef.current?.id.toString();

  const offset = useMemo((): Point | undefined => {
    const offsetDeskId = searchedRoomId ?? laskRoomId;
    if (!offsetDeskId) return;

    return bookingObjects.get(offsetDeskId);
  }, [bookingObjects, laskRoomId, searchedRoomId]);

  useEffect(() => {
    if (searchedRoomId) {
      dispatch(openRoom(searchedRoomId));
    }
  }, [dispatch, searchedRoomId, setSearchedBookingObject]);

  return (
    <FloorMapProvider
      floor={floor}
      backgroundData={backgroundData}
      floorLoading={loading}
      offset={offset}
    >
      <FullscreenContainer>
        <SidesheetSlot
          id='pageLevelModal'
          mode='modal'
          hasOverlay
          elevation='above'
        >
          <SidesheetSlot id='pageLevel'>
            <PageLayout />
          </SidesheetSlot>
        </SidesheetSlot>
      </FullscreenContainer>
    </FloorMapProvider>
  );
};

const Inner: FC = () => {
  return <InnerWithFloorMap />;
};

export const RoomBookingPage: FC = () => {
  const { building, floor } = useSelector(getRoomLocation);

  const dispatch = useAppDispatch();

  const setTown = useCallback(
    (value: Town | undefined) => {
      if (value) dispatch(roomsActions.setTown(value));
    },
    [dispatch],
  );

  const setBuilding = useCallback(
    (value: Building | undefined) => {
      if (value) dispatch(roomsActions.setBuilding(value));
    },
    [dispatch],
  );

  const setFloor = useCallback(
    (value: Floor | undefined) => {
      if (value) {
        dispatch(sidesheetActions.close());
        dispatch(roomsActions.setFloor(value));
      }
    },
    [dispatch],
  );

  const setTimeRange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dispatch(roomsActions.setTimeRange({ from, to }));
    },
    [dispatch],
  );

  return (
    <RoomBookingPageModeProvider>
      <SearchBookingObjectProvider
        setTown={setTown}
        setBuilding={setBuilding}
        setFloor={setFloor}
        setTimeRange={setTimeRange}
      >
        <FloorChipsProvider
          floor={floor}
          setFloor={setFloor}
          buildingId={building?.buildingId}
          filterItems={filterFloorItems}
        >
          <Inner />
        </FloorChipsProvider>
      </SearchBookingObjectProvider>
    </RoomBookingPageModeProvider>
  );
};

export default RoomBookingPage;
